import { useCallback } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import { useFela } from 'react-fela';
import { FormattedMessage, useIntl } from 'react-intl';

import type {
    ListGroupsAdministrationQueryError,
    ListGroupsAdministrationQueryResult,
    ListUsersAdministrationQueryError,
    ListUsersAdministrationQueryResult,
} from '@/modules/api/codegen';
import { SearchInput, SelectInput } from '@/modules/forms';
import { IconButton } from '@/modules/ui';

import { UserAdministrationType } from '../../constants';
import { useUserAdministrationStore } from '../../hooks/useUserAdministrationStore';
import { BatchActions } from './BatchActions';
import { useFilterSelectInputProps } from './hooks/useFilterSelectInputProps';
import * as felaRules from './UserAdministrationFilters.rules';

export type UserAdministrationFiltersProps =
    | {
          type: UserAdministrationType.Users;
          query: UseQueryResult<ListUsersAdministrationQueryResult, ListUsersAdministrationQueryError>;
      }
    | {
          type: UserAdministrationType.Groups;
          query: UseQueryResult<ListGroupsAdministrationQueryResult, ListGroupsAdministrationQueryError>;
      };

const allValue = 'all';

const availableFiltersByType = {
    [UserAdministrationType.Users]: ['status', 'groups', 'role'] as const,
    [UserAdministrationType.Groups]: ['status'] as const,
};

export const UserAdministrationFilters = ({ type, query }: UserAdministrationFiltersProps) => {
    const { css } = useFela();
    const { formatMessage } = useIntl();
    const { filters, setFilters, pagination, setPagination, selectedRows, setSelectedRows } = useUserAdministrationStore();

    const filterSelectProps = useFilterSelectInputProps();

    const handleFilterChange = useCallback(
        (filter: keyof typeof filterSelectProps, value: string) => {
            setFilters({ type, filters: { ...filters[type], [filter]: value === allValue ? undefined : value } });
        },
        [type, filters, setFilters],
    );

    const tableRecordsOnCurrentPage = query.data?.data.edges;
    const selectedTableRecordsOnCurrentPage = tableRecordsOnCurrentPage?.filter(record => selectedRows[type].includes(record.id!)) ?? [];

    const showBatchActions = !isEmpty(selectedTableRecordsOnCurrentPage);

    return (
        <div className={css(felaRules.container)}>
            <div className={css(felaRules.filtersContainer)}>
                {availableFiltersByType[type].map(filter => {
                    const filterValue = (filters[type] as Record<string, string | undefined>)[filter];

                    return (
                        <SelectInput
                            key={filter}
                            onChange={value => handleFilterChange(filter, value)}
                            value={filterValue ?? allValue}
                            customStyle={felaRules.selectButton}
                            labelRender={({ label }) => (
                                <FormattedMessage
                                    id={`user-administration.filters.${filter}`}
                                    values={{
                                        value: label,
                                    }}
                                />
                            )}
                            {...filterSelectProps[filter]}
                        />
                    );
                })}
            </div>

            {showBatchActions ? (
                <div className={css(felaRules.batchActionsContainer)}>
                    <BatchActions type={type} records={selectedTableRecordsOnCurrentPage} />
                    <IconButton type='ghost' icon='x' onClick={() => setSelectedRows({ type, selectedRowsKeys: [] })} />
                </div>
            ) : (
                <SearchInput
                    placeholder={formatMessage({ id: 'user-administration.filters.search.placeholder' })}
                    customStyle={felaRules.searchInput}
                    onChange={e => {
                        const searchValue = e.target.value === '' ? undefined : e.target.value;
                        setFilters({ type, filters: { ...filters[type], search: searchValue } });
                        setPagination({ type, pagination: { ...pagination[type], page: 1 } });
                    }}
                />
            )}
        </div>
    );
};
